// Vyčištění lokálního stavu při přihlášení / odhlášení řidiče.
//
// PROBLÉM (do 2026-08): `login()` uložil jen token a nastavil drivera. Nemazal
// TanStack Query cache (`route/today` má staleTime), zustand stores ani Dexie.
// Řidič, který se přihlásil na telefonu po jiném řidiči, tak viděl CIZÍ trasu
// a musel se to řešit ručním mazáním mezipaměti.
//
// Pravidlo: cache (query, trasa, směna, progress) se maže VŽDY. Neodeslaná data
// (pendingEvents, pendingGps) se mažou JEN při změně řidiče — u stejného řidiče
// by se tím zahodila práce, která ještě nedošla na server.

import { Preferences } from "@capacitor/preferences";
import { queryClient } from "./queryClient";
import { clearOfflineCaches, clearPendingQueues } from "./db";
import { LAST_DRIVER_KEY, LAST_PATH_KEY } from "./storageKeys";
import { useShiftStore } from "../store/shiftStore";
import { useStopProgressStore } from "../store/stopProgressStore";
import { useMaterialStore } from "../store/materialStore";
import { useStornoAckStore } from "../store/stornoAckStore";
import { useGpsStore } from "../store/gpsStore";
import { useSyncStore } from "../store/syncStore";

/** ID naposledy přihlášeného řidiče. Preferences (přežije kill app), s fallbackem
 *  na localStorage pro PWA. */
export async function getLastDriverId(): Promise<string | null> {
  try {
    const { value } = await Preferences.get({ key: LAST_DRIVER_KEY });
    if (value) return value;
  } catch { /* ignore */ }
  try { return localStorage.getItem(LAST_DRIVER_KEY); } catch { return null; }
}

export async function setLastDriverId(driverId: string): Promise<void> {
  try { await Preferences.set({ key: LAST_DRIVER_KEY, value: driverId }); } catch { /* ignore */ }
  try { localStorage.setItem(LAST_DRIVER_KEY, driverId); } catch { /* ignore */ }
}

/**
 * Zahodí lokální stav předchozí session.
 *
 * @param wipePendingQueues true = smazat i neodeslané události a GPS body
 *        (volat POUZE při změně řidiče, viz komentář nahoře).
 */
export async function resetSession(
  { wipePendingQueues }: { wipePendingQueues: boolean },
): Promise<void> {
  // 1) GPS tracking předchozí směny — jinak by běžel dál a psal body pod cizí shift_id.
  try { await useGpsStore.getState().stop(); } catch { /* ignore */ }

  // 2) In-memory stores
  useShiftStore.getState().clear();
  useStopProgressStore.getState().reset();
  useMaterialStore.getState().clear();
  try { await useStornoAckStore.getState().reset(); } catch { /* ignore */ }

  // 3) Server cache (route/today, shift, reporty…)
  queryClient.clear();

  // 4) Offline cache v Dexie + volitelně neodeslané fronty
  try {
    await clearOfflineCaches();
    if (wipePendingQueues) await clearPendingQueues();
  } catch (e) {
    console.warn("[sessionReset] Dexie cleanup selhal:", e);
  }

  // 5) Poslední cesta — jinak by nový řidič skončil na detailu cizí zastávky
  try { localStorage.removeItem(LAST_PATH_KEY); } catch { /* ignore */ }

  // 6) Sjednotit počítadlo ve status baru s reálným obsahem fronty
  try { await useSyncStore.getState().refreshCount(); } catch { /* ignore */ }
}

/**
 * Best-effort pokus odeslat frontu, dokud ještě platí token PŮVODNÍHO řidiče.
 * Volá se před výměnou tokenu při loginu — po výměně by backend odmítl události
 * cizí směny. Nikdy nehází; s prázdnou frontou se vrací hned.
 */
export async function flushBeforeLogin(timeoutMs = 5_000): Promise<void> {
  try { await useSyncStore.getState().refreshCount(); } catch { /* ignore */ }
  const sync = useSyncStore.getState();
  if (!sync.online || sync.pendingCount === 0) return;
  try {
    await Promise.race([
      sync.syncNow(),
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  } catch { /* offline / chyba serveru — data zůstanou ve frontě */ }
}
